import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, BadgeCheck, Gavel, Scale, MapPin, Sparkles, ShieldCheck, Lock, Wallet, FileCheck2, HeartHandshake, Landmark, ScrollText, Play } from "lucide-react";
import HallOfFounders from "@/components/HallOfFounders";
import HomeShowcase from "@/components/HomeShowcase";
import { useConfig } from "@/contexts/ConfigContext";
import api from "@/lib/api";

const SKYLINE = "https://images.unsplash.com/photo-1776855492307-617c4c91baef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwyfHxzYW50aWFnbyUyMGNoaWxlJTIwc2t5bGluZSUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3NzcyMjAzMDF8MA&ixlib=rb-4.1.0&q=85";

const CLP_COMPACT = (n) => {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1).replace(".0", "")}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n}`;
};

// Cache-buster para el logo
const LOGO_URL = "/logo.png";

export default function Landing() {
  const { config } = useConfig();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/stats/public").then((r) => {
      if (!cancelled) setStats(r.data);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  return (
    <div data-testid="landing-page">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="container-app pt-16 lg:pt-24 pb-16 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7 reveal">
            <div className="label-eyebrow mb-6" data-testid="hero-eyebrow">LegalTech · Chile · Conecta · Delega · Resuelve</div>
            <h1 className="serif text-4xl md:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight" data-testid="hero-title">
              La red que conecta a{" "}
              <span className="brand-gradient-text">abogados y mediadores certificados</span>{" "}
              con pagos y gestiones{" "}
              <span className="text-amber-300">100% seguras.</span>
            </h1>
            <p className="mt-8 text-lg text-muted-foreground max-w-xl leading-relaxed" data-testid="hero-subtitle">
              Contrata, delega y cobra dentro de un ecosistema con verificación real ante el Poder Judicial, pagos custodios en escrow y liberación garantizada solo cuando el trabajo se entrega. Nadie huye con tus honorarios, ni con tu confianza.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3" data-testid="hero-ctas">
              {/* 1. Soy Abogado/a — gradiente verde/cian (destacado) */}
              <Link
                to="/registro/abogado"
                className="group inline-flex items-center gap-2 px-5 py-3 rounded-md font-medium text-sm text-emerald-950 bg-gradient-to-r from-emerald-300 via-teal-300 to-cyan-300 hover:from-emerald-200 hover:via-teal-200 hover:to-cyan-200 shadow-[0_0_0_1px_rgba(94,234,212,0.5),0_8px_24px_-8px_rgba(94,234,212,0.55)] transition-all"
                data-testid="hero-cta-abogado"
              >
                Soy Abogado/a
                <ArrowUpRight size={14} className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"/>
              </Link>

              {/* 2. Soy Mediador/a — outline azul oscuro */}
              <Link
                to="/registro/mediador"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-md font-medium text-sm text-white bg-slate-950/70 border border-white/15 hover:bg-slate-900/80 hover:border-white/30 transition-colors"
                data-testid="hero-cta-mediador"
              >
                <Scale size={15} className="text-cyan-200"/>
                Soy Mediador/a
              </Link>

              {/* 3. Necesito Mediación — outline dorado suave */}
              <Link
                to="/registro/solicitante"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-md font-medium text-sm text-amber-200 bg-slate-950/70 border border-amber-400/40 hover:bg-amber-400/10 hover:border-amber-400/60 transition-colors"
                data-testid="hero-cta-solicitante"
              >
                <HeartHandshake size={15} className="text-amber-300"/>
                Necesito Mediación
              </Link>
            </div>

            <a href="#como-funciona" className="mt-4 inline-flex text-sm text-cyan-300 hover:text-cyan-200 underline decoration-dotted underline-offset-4 items-center gap-1.5" data-testid="hero-cta-secondary">
              <Play size={12}/> Cómo funciona
            </a>

            {/* Contador global vivo */}
            {stats && (
              <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-4 max-w-xl" data-testid="hero-stats">
                <StatBlock
                  label="Profesionales verificados"
                  value={stats.professionals}
                  testid="stat-professionals"
                />
                <StatBlock
                  label="Gestiones protegidas"
                  value={stats.protected_operations}
                  testid="stat-operations"
                />
                <StatBlock
                  label="En transacciones seguras"
                  value={CLP_COMPACT(stats.protected_operations_clp)}
                  raw
                  testid="stat-amount"
                />
              </div>
            )}

            {/* Trust bar institucional */}
            <div className="mt-10 flex flex-wrap items-center gap-3 sm:gap-4" data-testid="trust-bar">
              <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground/70">Verificamos ante</span>
              <TrustBadge icon={Landmark} label="Poder Judicial" />
              <TrustBadge icon={ScrollText} label="Ministerio de Justicia" />
              <TrustBadge icon={BadgeCheck} label="Corte Suprema" />
            </div>
          </div>

          {/* Trust / illustration — columna derecha */}
          <div className="lg:col-span-5 reveal reveal-2">
            <div className="relative flex justify-center">
              <div className="absolute inset-0 -m-8 blur-3xl opacity-30 pointer-events-none" style={{ background: "var(--grad-brand)" }} />
              <img src="/logo.png" alt="Colega Link" className="relative w-[360px] h-[360px] rounded-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Showcase */}
      <div id="como-funciona">
        <HomeShowcase />
      </div>

      {/* Stats / pillars */}
      <section className="border-y" style={{ borderColor: "hsl(var(--border))" }}>
        <div className="container-app grid grid-cols-2 md:grid-cols-4 grid-borders">
          {[
            { k: "Topes resueltos", v: "en minutos", icon: Sparkles },
            { k: "Cobertura nacional", v: "16 regiones", icon: MapPin },
            { k: "Mediadores", v: "verificados", icon: BadgeCheck },
            { k: "Pago", v: "Protegido", icon: ShieldCheck },
          ].map((s) => (
            <div key={s.k} className="p-8">
              <s.icon size={18} className="text-[hsl(var(--primary))]"/>
              <div className="mt-4 serif text-3xl">{s.v}</div>
              <div className="mt-1 label-eyebrow">{s.k}</div>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="como-funciona" className="container-app py-24">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="label-eyebrow">Cómo funciona</div>
            <h2 className="serif text-4xl md:text-5xl mt-3 leading-tight">Flujo seguro de extremo a extremo.</h2>
            <p className="mt-6 text-muted-foreground leading-relaxed">
              Publicas, confirmas el Pago Protegido, subes los antecedentes, tu colega ejecuta y recibes el comprobante.
              Solo entonces se libera el pago. Sin filtración de datos de contacto, sin sorpresas.
            </p>
          </div>
          <div className="lg:col-span-8 grid md:grid-cols-2 gap-px bg-[hsl(var(--border))]">
            {[
              { t: "Publica la audiencia", d: "Tribunal, fecha, hora, monto y observaciones. Menos de 90 segundos.", icon: Gavel },
              { t: "Asigna y activa el Pago Protegido", d: "Eliges al colega que postula. El pago + cargo Colega Link queda retenido.", icon: Wallet },
              { t: "Sube los antecedentes", d: "Carpeta del caso y delega poder firmado. Solo el delegado los ve.", icon: ShieldCheck },
              { t: "Liberas contra entrega", d: "El colega sube el comprobante. Apruebas y se libera el pago.", icon: FileCheck2 },
            ].map((s, i) => (
              <div key={s.t} className="bg-background p-8">
                <s.icon size={20} className="text-[hsl(var(--primary))]"/>
                <div className="mt-6 serif text-2xl">{s.t}</div>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.d}</p>
                <div className="mt-6 label-eyebrow">0{i + 1}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Anonymous operation */}
      <section className="border-y" style={{ borderColor: "hsl(var(--border))" }}>
        <div className="container-app py-20 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <div className="label-eyebrow text-[hsl(var(--accent))]">Operación anónima</div>
            <h2 className="serif text-4xl md:text-5xl mt-3 leading-tight">El abogado toma la delegación.<br/>Toda la gestión continúa dentro de Colega Link.</h2>
            <p className="mt-6 text-muted-foreground max-w-xl leading-relaxed">
              No se comparten teléfonos, correos ni contactos hasta que el caso lo requiere.
              Archivos cifrados, comunicación intermediada, pagos auditados. Tu cartera no se filtra.
            </p>
          </div>
          <div className="lg:col-span-6">
            <div className="panel-glow p-8">
              <div className="flex items-center gap-3"><Lock size={20} className="text-[hsl(var(--primary))]"/><span className="label-eyebrow">Garantías del sistema</span></div>
              <ul className="mt-6 space-y-3 text-sm">
                <li className="flex items-start gap-3"><BadgeCheck size={16} className="text-[hsl(var(--accent))] mt-0.5"/>Identidades verificadas manualmente por administración</li>
                <li className="flex items-start gap-3"><ShieldCheck size={16} className="text-[hsl(var(--accent))] mt-0.5"/>Pago retenido hasta entrega del comprobante de gestión</li>
                <li className="flex items-start gap-3"><FileCheck2 size={16} className="text-[hsl(var(--accent))] mt-0.5"/>Reseñas bidireccionales con auditoría</li>
                <li className="flex items-start gap-3"><Scale size={16} className="text-[hsl(var(--accent))] mt-0.5"/>Comisión preferente para Miembros Fundadores</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Mediadores */}
      <section id="mediadores" className="container-app py-24 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6 order-2 lg:order-1">
          <img src={SKYLINE} alt="Chile" className="rounded-lg w-full h-[420px] object-cover ring-1 ring-white/10"/>
        </div>
        <div className="lg:col-span-6 order-1 lg:order-2">
          <div className="label-eyebrow">Directorio nacional</div>
          <h2 className="serif text-4xl md:text-5xl mt-3">Mediadores familiares, ordenados por región.</h2>
          <p className="mt-6 text-muted-foreground max-w-lg leading-relaxed">
            Encuentra mediadores inscritos en el Ministerio de Justicia, por comuna, especialidad y reputación.
            Perfiles estandarizados, contacto intermediado, cero llamados a oficinas que no contestan.
          </p>
          <Link to="/registro/abogado" className="btn-ghost mt-8" data-testid="cta-explore-mediators">Crear cuenta para explorar</Link>
        </div>
      </section>

      {/* Hall of Founders */}
      <HallOfFounders />
    </div>
  );
}

function StatBlock({ label, value, raw = false, testid }) {
  const display = raw ? value : (typeof value === "number" ? value.toLocaleString("es-CL") : value);
  return (
    <div className="panel px-4 py-3.5 bg-white/[0.02] border-white/10" data-testid={testid}>
      <div className="serif text-2xl md:text-3xl text-cyan-100 leading-none">{display || "0"}</div>
      <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground/80 mt-1.5">{label}</div>
    </div>
  );
}

function TrustBadge({ icon: Icon, label }) {
  return (
    <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white/[0.03] border border-white/10 text-xs text-muted-foreground">
      <Icon size={13} className="text-cyan-300/80"/>
      <span>{label}</span>
    </div>
  );
}
